import { AccountReceivablesService, IndicatorScoreService, ScoreLogicService } from '@/api';
import Breadcrumb from '@/components/breadcrumb';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.min.css'; // Optional Theme applied to the grid
import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

export type IndicatorScorePageProps = {
  organizationId: string;
  scoreIndicatorId: string;
};

interface MarkdownTextProps {
  text: string;
} 

export const IndicatorScorePage = ({
  organizationId,
  scoreIndicatorId,
}: IndicatorScorePageProps) => {
  const [isOpen, setOpen] = useState(false);
  // get the current organization

  const org = useStore($org);
  // get the current project
  const ar = useStore($accountreceivable);


  // breadcrumb pages
  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name
        ? `/organization/${org?.id}/score_logics`
        : '/organizations',
    },
    {
      label: ar?.name || 'Score Logic',
      href: `/organization/${org?.id}/indicatorscores/${scoreIndicatorId}`,
    },
  ];

  const { theme } = useTheme();

  // get the account receivable
  // need to create controller for this
  const scoreLogicResult = useQuery({
    queryKey: ['indicatorScoreResult', organizationId, scoreIndicatorId],
    queryFn: async () => {
      const result = await IndicatorScoreService.getIndicatorScore(
        scoreIndicatorId,
        organizationId,
      );

      if (!result) {
        throw new Error('No data found');
      }
      return result;
    },
  });


  const MarkdownText: React.FC<MarkdownTextProps> = ({ text }) => {
    // Function to convert markdown-like text to HTML
    const convertToHTML = (text: string): string => {
      // Replace '**Header**' with HTML <h1> or <h2> tags
      const formattedText = text
        .replace(/\*\*(.*?)\*\*/g, (_, header) => `<h2>${header}</h2>`) // Change <h2> to <h1> for main headers if needed
        .replace(/\n/g, '<br/>'); // Replace line breaks with <br> tags

      return formattedText;
    };

    // Convert text to HTML
    const htmlText = convertToHTML(text);

    return <div dangerouslySetInnerHTML={{ __html: htmlText }} />;
  };
  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>
      {/* Page Header */}
      <div className="mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
          {`${scoreLogicResult.data?.indicatorScore?.accountReceivable?.name || 'Indicator Score details'} score history`}
          </h2>
        </div>
      </div>
      {/* Score History Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Previous Score</TableHead>
              <TableHead>New Score</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Event</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scoreLogicResult.data?.scoreHistory?.map((history) => (
              <TableRow key={history.id}>
                <TableCell>
                  {new Date(history.eventDate ?? history.createdAtUtc ?? Date.now()).toLocaleString()}
                </TableCell>
                <TableCell>{history.previousScore}</TableCell>
                <TableCell>{history.newScore}</TableCell>
                <TableCell>
                  {((history?.newScore ?? 0) - (history?.previousScore ?? 0)).toFixed(2)}
                </TableCell>
                <TableCell>{history?.eventName}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
